import React, { useEffect, useRef } from 'react';
import { Mic, MicOff, Video, VideoOff, ShieldCheck, Users } from 'lucide-react';

interface PreJoinProps {
  userName: string;
  isGuest: boolean;
  meetingCode: string;
  stream: MediaStream | null;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  /** Toplantı sahibi mi? (bekleme odası ayarını yalnızca sahibi görür) */
  isOwner: boolean;
  waitingRoomEnabled: boolean;
  onWaitingRoomChange: (enabled: boolean) => void;
  onJoin: () => void;
  /** Sahiplik doğrulaması sürerken katılım butonu kilitli kalır */
  joinDisabled?: boolean;
  onCancel: () => void;
}

const PreJoin: React.FC<PreJoinProps> = ({
  userName,
  isGuest,
  meetingCode,
  stream,
  isVideoEnabled,
  isAudioEnabled,
  onToggleVideo,
  onToggleAudio,
  isOwner,
  waitingRoomEnabled,
  onWaitingRoomChange,
  onJoin,
  joinDisabled = false,
  onCancel,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (el && stream) {
      el.srcObject = stream;
    }
  }, [stream, isVideoEnabled]);

  const initials = userName
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="fixed inset-0 bg-slate-950 overflow-y-auto">
      {/* Arka plan süsleri */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-purple-600/20 rounded-full blur-3xl -top-48 -left-48" />
        <div className="absolute w-96 h-96 bg-blue-600/20 rounded-full blur-3xl -bottom-48 -right-48" />
      </div>

      <div className="relative min-h-full flex items-center justify-center p-4 py-6 sm:py-8">
        <div className="w-full max-w-4xl flex flex-col md:grid md:grid-cols-[1.4fr_1fr] gap-4 md:gap-6 md:items-stretch">
          {/* Video önizleme: mobilde dikey (3:4), masaüstünde yatay (16:9) */}
          <div className="relative w-full max-w-[320px] sm:max-w-sm mx-auto md:max-w-none md:mx-0 aspect-[3/4] md:aspect-video rounded-2xl overflow-hidden bg-slate-900 ring-1 ring-white/10 shadow-2xl">
            {stream && isVideoEnabled ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover -scale-x-100"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold">
                  {initials || '?'}
                </div>
              </div>
            )}

            {!stream && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
                <span className="text-gray-300 text-sm animate-pulse">Kamera başlatılıyor...</span>
              </div>
            )}

            {/* İsim etiketi — kontrollerle çakışmasın diye üstte */}
            <div className="absolute top-3 left-3 max-w-[calc(100%-1.5rem)] px-3 py-1 rounded-lg bg-black/60 backdrop-blur-sm text-white text-xs sm:text-sm font-medium truncate">
              {userName}
              {isGuest && <span className="text-gray-400"> (Misafir)</span>}
            </div>

            {/* Mikrofon / kamera kontrolleri */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4">
              <button
                type="button"
                onClick={onToggleAudio}
                className={`p-3.5 sm:p-4 rounded-full transition active:scale-95 shadow-lg ${
                  isAudioEnabled
                    ? 'bg-white/15 hover:bg-white/25 text-white backdrop-blur'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
                aria-label={isAudioEnabled ? 'Mikrofonu kapat' : 'Mikrofonu aç'}
                title={isAudioEnabled ? 'Mikrofonu kapat' : 'Mikrofonu aç'}
              >
                {isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </button>
              <button
                type="button"
                onClick={onToggleVideo}
                className={`p-3.5 sm:p-4 rounded-full transition active:scale-95 shadow-lg ${
                  isVideoEnabled
                    ? 'bg-white/15 hover:bg-white/25 text-white backdrop-blur'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
                aria-label={isVideoEnabled ? 'Kamerayı kapat' : 'Kamerayı aç'}
                title={isVideoEnabled ? 'Kamerayı kapat' : 'Kamerayı aç'}
              >
                {isVideoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              </button>
            </div>
          </div>

        {/* Sağ panel */}
          <div className="bg-slate-900/90 backdrop-blur rounded-2xl ring-1 ring-white/10 p-4 sm:p-6 flex flex-col justify-center gap-4 sm:gap-5 shadow-2xl">
            <div className="text-center md:text-left">
              <h1 className="text-white text-xl sm:text-2xl font-bold mb-1">Katılmaya hazır mısınız?</h1>
              <p className="text-gray-400 text-sm">
                Toplantı kodu: <span className="text-purple-300 font-mono">{meetingCode}</span>
              </p>
            </div>

          {/* Bekleme odası ayarı — sadece toplantı sahibi */}
          {isOwner && (
            <label className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition">
              <input
                type="checkbox"
                checked={waitingRoomEnabled}
                onChange={(e) => onWaitingRoomChange(e.target.checked)}
                className="sr-only peer"
              />
              <span
                className={`mt-0.5 relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition ${
                  waitingRoomEnabled ? 'bg-purple-600' : 'bg-white/20'
                }`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                    waitingRoomEnabled ? 'left-[22px]' : 'left-0.5'
                  }`}
                />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-white text-sm font-semibold">
                  <ShieldCheck className="w-4 h-4 text-purple-400" />
                  Bekleme odası
                </span>
                <span className="block text-gray-400 text-xs mt-0.5">
                  {waitingRoomEnabled
                    ? 'Katılımcılar ancak siz onayladıktan sonra toplantıya girebilir.'
                    : 'Kapalı — katılımcılar doğrudan toplantıya girer.'}
                </span>
              </span>
            </label>
          )}

          {!isOwner && (
            <div className="flex items-center gap-2 text-gray-400 text-xs p-3 rounded-xl bg-white/5 border border-white/10">
              <Users className="w-4 h-4 text-purple-400 flex-shrink-0" />
              Toplantı sahibi bekleme odasını açtıysa katılımınız onaya düşer.
            </div>
          )}

          <div className="space-y-2">
            <button
              type="button"
              onClick={onJoin}
              disabled={joinDisabled}
              className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold transition active:scale-[0.99] shadow-lg"
            >
              {joinDisabled ? 'Hazırlanıyor...' : isOwner ? 'Toplantıyı başlat' : 'Şimdi katıl'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="w-full px-4 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-xl font-medium transition"
            >
              Vazgeç
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreJoin;
